import React, { useCallback, useMemo } from 'react';
import { Reducer, State, initialState } from './reducer';
import { useLocalStorage } from 'react-use';
import { StoreAndGroupType } from './context.types';
import { AxiosInstance } from 'axios';
import createAPIClient from '@/apiClient/apiClient';
import { QueryClient } from '@tanstack/react-query';

export interface STKProviderConfig {
  loginUrl: string;
  apiUrl: string;
  disableAuth: boolean;
}

export interface ProviderState extends State {
  setStores: (items: string[]) => void;
  setStoresAndPages: ({ storeNumber, page }: { storeNumber: string; page: number }) => void;
  setStoresAndGroups: (storeAndGroupType: StoreAndGroupType) => void;
  stores: string[];
  storesAndPages: Map<string, number>;
  storesAndGroups: StoreAndGroupType;
  apiClient: AxiosInstance;
  config: STKProviderConfig;
}

export const STKContext = React.createContext<ProviderState | undefined>(undefined);

STKContext.displayName = 'STKContext';

export const useSTKContext = () => {
  const context = React.useContext(STKContext);
  if (context === undefined) {
    throw new Error(`use must be used within a Provider`);
  }
  return React.useMemo(() => context, [context]);
};

export const STKProvider: React.FC<{
  children?: React.ReactNode;
  state?: State;
  config: STKProviderConfig;
  queryClient: QueryClient;
  apiClient?: AxiosInstance;
}> = (props) => {
  const { state: localState, config, queryClient, apiClient } = props;
  // If localState is provided, use it directly; otherwise use localStorage
  const [savedData, saveData] = useLocalStorage('STK_DATA', JSON.stringify(localState || initialState));
  const initialReducerState = localState || JSON.parse(savedData!);
  const [state, dispatch] = React.useReducer(Reducer, initialReducerState);

  const localApiClient = useMemo(
    () => apiClient ?? createAPIClient(config.apiUrl, config.disableAuth, queryClient),
    [apiClient, config.apiUrl, config.disableAuth],
  );

  const setStores = useCallback((stores: string[]) => dispatch({ type: 'SET_STORES', stores }), []);

  const setStoresAndPages = useCallback(
    (storeAndPage: { storeNumber: string; page: number }) => dispatch({ type: 'SET_STORES_AND_PAGES', storeAndPage }),
    [],
  );

  const setStoresAndGroups = useCallback(
    (storesAndGroups: StoreAndGroupType) => dispatch({ type: 'SET_STORES_AND_GROUP', storesAndGroups }),
    [],
  );

  React.useEffect(() => {
    saveData(JSON.stringify(state));
  }, [state, state.storesAndPages, state.storesAndGroups, saveData]);

  const value = React.useMemo(
    () => ({
      ...state,
      storesAndPages: state.storesAndPages || new Map(),
      storesAndGroups: state.storesAndGroups,
      setStores,
      setStoresAndPages,
      setStoresAndGroups,
      config,
      apiClient: localApiClient,
    }),
    [setStores, setStoresAndPages, setStoresAndGroups, state, config, localApiClient],
  );
  return <STKContext.Provider value={value} {...props} />;
};
